import type { ElementType } from 'react';
import { forwardRef } from 'react';

import { classNames } from '~/utils/styles';

import type { TextProps, TextVariant } from './Text.types';
import { textColorClassNames } from './textColor';

const variantClassNames: Record<TextVariant, string> = {
  body: 'body-200-light',
  'body-sm': 'body-250-light',
  caption: 'body-300-light',
  label: 'label-200',
  'label-sm': 'label-300',
  lead: 'body-100-light',
  micro: 'label-400',
  numbers: 'numbers-100',
  value: 'body-200',
};

// Medium-weight variants have no -heavy token, so bold uses a font-weight utility instead
// (utilities always win over the token's @layer components weight).
const boldVariantClassNames: Record<TextVariant, string> = {
  body: 'body-200-heavy',
  'body-sm': 'body-250-heavy',
  caption: 'body-300-heavy',
  label: 'label-200 font-semibold',
  'label-sm': 'label-300 font-semibold',
  lead: 'body-100-heavy',
  micro: 'label-400 font-semibold',
  numbers: 'numbers-100 font-semibold',
  value: 'body-200-heavy',
};

/**
 * Typography primitive for all non-heading text. `variant`/`bold`/`color` own the typography —
 * `className` is for layout only (spacing, truncation, flex sizing).
 */
export const Text = forwardRef<HTMLElement, TextProps>(
  ({ as = 'div', variant = 'body', bold = false, color = 'inherit', className, children, ...props }, forwardedRef) => {
    const Element = as as ElementType;
    const classes = classNames(
      bold ? boldVariantClassNames[variant] : variantClassNames[variant],
      textColorClassNames[color],
      className,
    );

    return (
      <Element className={classes} ref={forwardedRef} {...props}>
        {children}
      </Element>
    );
  },
);
